import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { ViewportOcclusionRect } from '../../models/types';
import FloatingWorkspaceCard from '../workspace/FloatingWorkspaceCard';
import ExecutionTimelineComparisonStrip from './ExecutionTimelineComparisonStrip';
import { buildExecutionTimelineModel } from './executionTimelineModel';
import type { ExecutionActivityViewModel, ExecutionTripViewModel } from './executionTypes';

interface ExecutionComparisonTimelineProps {
  trip: ExecutionTripViewModel;
  drawerWidth: number;
  activeActivityId: string | null;
  hoveredActivityId: string | null;
  bottomSheet?: boolean;
  onClose: () => void;
  onFocusActivity: (activity: ExecutionActivityViewModel) => void;
  onHoverActivity: (activityId: string | null) => void;
  onOcclusionRectChange?: (rect: ViewportOcclusionRect | null) => void;
}

const ExecutionComparisonTimeline: React.FC<ExecutionComparisonTimelineProps> = ({
  trip,
  drawerWidth,
  activeActivityId,
  hoveredActivityId,
  bottomSheet = false,
  onClose,
  onFocusActivity,
  onHoverActivity,
  onOcclusionRectChange,
}) => {
  const model = useMemo(() => buildExecutionTimelineModel(trip), [trip]);
  const defaultX = typeof window === 'undefined' ? drawerWidth + 32 : Math.max(drawerWidth + 16, window.innerWidth - 960);
  const defaultY = typeof window === 'undefined' ? 600 : Math.max(100, window.innerHeight - 310);

  return (
    <FloatingWorkspaceCard
      key={trip.id}
      title="Planned vs actual Timeline"
      subtitle={`${trip.id} · ${trip.plannedTrip.timezone}`}
      defaultPosition={{ x: defaultX, y: defaultY }}
      defaultSize={{ width: 930, height: 278 }}
      minSize={{ width: 620, height: 248 }}
      maxSize={{ width: 1380, height: 420 }}
      persistenceKey="trips-execution-comparison-timeline-v2"
      accentColor={trip.color}
      onClose={onClose}
      onOcclusionRectChange={onOcclusionRectChange}
      layoutMode={bottomSheet ? 'bottom-sheet' : 'floating'}
      bottomSheetHeight={278}
      contentSx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      testId="execution-comparison-timeline"
    >
      <Stack direction="row" spacing={1.3} alignItems="center" sx={{ px: 1.2, minHeight: 30, borderBottom: '1px solid', borderColor: 'divider' }} data-floating-no-drag="true">
        <Stack direction="row" spacing={0.45} alignItems="center"><Box sx={{ width: 20, borderTop: '2px dashed #64748b' }} /><Typography sx={{ fontSize: 10.5 }}>Planned</Typography></Stack>
        <Stack direction="row" spacing={0.45} alignItems="center"><Box sx={{ width: 20, borderTop: '4px solid', borderColor: trip.color }} /><Typography sx={{ fontSize: 10.5, fontWeight: 750 }}>Actual</Typography></Stack>
        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Blue travel · thick service/dwell · connector shows arrival variance</Typography>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: 1.1, pt: 0.45 }} data-floating-no-drag="true">
        <ExecutionTimelineComparisonStrip
          trip={trip}
          model={model}
          activeActivityId={activeActivityId}
          hoveredActivityId={hoveredActivityId}
          onFocusActivity={onFocusActivity}
          onHoverActivity={onHoverActivity}
        />
      </Box>
    </FloatingWorkspaceCard>
  );
};

export default ExecutionComparisonTimeline;
